/**
 * System Onboarding Templates
 * 
 * Industry-specific CRM configuration templates
 * Based on the original plan: Automotive, Insurance, SaaS
 */

export interface OnboardingTemplate {
  id: string;
  industry: string;
  name: string;
  description: string;
  icon: string;  // Icon name for UI
  
  // Pre-configured products (will be created in CRM)
  defaultProducts: Array<{
    name: string;
    key: string;
    type: 'consultancy' | 'development' | 'solution' | 'service';
    pricing?: {
      model: 'one_time' | 'recurring';
      range?: { min: number; max: number };
    };
  }>;
  
  // Pipeline stages
  leadStages: string[];
  opportunityStages: string[];
  
  // Sample data option
  sampleData: {
    available: boolean;
    description: string;
    includes: string[];  // "3 sample companies", "5 sample leads"
  };
  
  // Module alignment
  requiredModules: string[];
  recommendedModules: string[];
}

// ============================================================================
// System Templates
// ============================================================================

export const TEMPLATES: OnboardingTemplate[] = [
  {
    id: 'finance',
    industry: 'finance',
    name: 'Bank / Investment',
    description: 'Banken und Investment-Beratung (B2C, Beratungsgespräche & Termine)',
    icon: 'Landmark',
    
    // Finance Products - Focus on consultation and appointments
    defaultProducts: [
      { name: 'Girokonto', key: 'checking_account', type: 'service', pricing: { model: 'recurring', range: { min: 0, max: 15 } } },
      { name: 'Sparkonto', key: 'savings_account', type: 'service', pricing: { model: 'recurring', range: { min: 0, max: 10 } } },
      { name: 'Kreditkarte', key: 'credit_card', type: 'service', pricing: { model: 'recurring', range: { min: 0, max: 50 } } },
      { name: 'Privatkredit', key: 'personal_loan', type: 'service', pricing: { model: 'one_time', range: { min: 1000, max: 100000 } } },
      { name: 'Baufinanzierung', key: 'mortgage', type: 'service', pricing: { model: 'one_time', range: { min: 50000, max: 1000000 } } },
      { name: 'Investment-Beratung', key: 'investment', type: 'consultancy', pricing: { model: 'recurring', range: { min: 0, max: 500 } } },
      { name: 'Altersvorsorge', key: 'retirement', type: 'consultancy', pricing: { model: 'recurring', range: { min: 50, max: 500 } } },
      { name: 'Vermögensberatung', key: 'wealth_mgmt', type: 'consultancy', pricing: { model: 'recurring', range: { min: 100, max: 2000 } } },
      { name: 'Depot-Eröffnung', key: 'depot', type: 'service', pricing: { model: 'one_time', range: { min: 0, max: 100 } } }
    ],
    
    leadStages: ['Online-Anfrage', 'Termin vereinbart', 'Beratung durchgeführt', 'Angebot erstellt', 'Kunde'],
    opportunityStages: ['Erstgespräch', 'Bedarfsanalyse', 'Angebot', 'Vertragsunterzeichnung', 'Abgeschlossen'],
    
    sampleData: {
      available: true,
      description: 'Bank & Investment Beispieldaten mit Beratungsterminen',
      includes: ['12 Beratungsanfragen', '8 gebuchte Termine', '5 Neu-Kunden', '3 Investment-Beratungen']
    },
    
    requiredModules: ['crm_core'],
    recommendedModules: []  // Finance usually doesn't need Google Ads (regulated industry)
  },
  
  {
    id: 'agency',
    industry: 'agency',
    name: 'Marketing Agentur',
    description: 'Marketing-, Design- und Werbe-Agenturen (B2B, Klienten-Beziehungen)',
    icon: 'Palette',
    
    // Agency Services (what they sell to clients)
    defaultProducts: [
      { name: 'Social Media Marketing', key: 'social_media', type: 'consultancy', pricing: { model: 'recurring', range: { min: 1000, max: 5000 } } },
      { name: 'Google Ads Management', key: 'google_ads_mgmt', type: 'consultancy', pricing: { model: 'recurring', range: { min: 1500, max: 10000 } } },
      { name: 'SEO Services', key: 'seo', type: 'consultancy', pricing: { model: 'recurring', range: { min: 800, max: 4000 } } },
      { name: 'Content Marketing', key: 'content', type: 'consultancy', pricing: { model: 'recurring', range: { min: 1200, max: 6000 } } },
      { name: 'Brand Strategy', key: 'brand_strategy', type: 'consultancy', pricing: { model: 'one_time', range: { min: 5000, max: 30000 } } },
      { name: 'Web Design', key: 'web_design', type: 'development', pricing: { model: 'one_time', range: { min: 3000, max: 20000 } } },
      { name: 'Marketing Automation', key: 'automation', type: 'consultancy', pricing: { model: 'recurring', range: { min: 2000, max: 8000 } } },
      { name: 'Performance Marketing', key: 'performance', type: 'consultancy', pricing: { model: 'recurring', range: { min: 2500, max: 12000 } } }
    ],
    
    leadStages: ['Erstkontakt', 'Briefing geplant', 'Pitch', 'Verhandlung', 'Klient'],
    opportunityStages: ['Anfrage', 'Briefing', 'Proposal', 'Verhandlung', 'Auftrag'],
    
    sampleData: {
      available: true,
      description: 'Marketing Agentur Beispieldaten mit Klienten und laufenden Kampagnen',
      includes: ['6 potenzielle Klienten', '4 laufende Projekte', '8 Proposals', '3 aktive Kampagnen']
    },
    
    requiredModules: ['crm_core'],
    recommendedModules: ['time_tracking', 'google_ads']
  },
  
  {
    id: 'ecommerce',
    industry: 'ecommerce',
    name: 'E-Commerce / Online Shop',
    description: 'Online-Shops mit Kundenbeziehungs-Management (B2C, Verkaufshistorie)',
    icon: 'ShoppingCart',
    
    // E-Commerce - Focus on customer interactions, not shop products
    // Shop products are in Shopify/WooCommerce, CRM tracks customer relationships
    defaultProducts: [
      { name: 'Online-Bestellung', key: 'online_order', type: 'service', pricing: { model: 'one_time', range: { min: 20, max: 5000 } } },
      { name: 'Produkt-Anfrage', key: 'product_inquiry', type: 'service', pricing: { model: 'one_time', range: { min: 0, max: 0 } } },
      { name: 'Kundenservice-Fall', key: 'support_case', type: 'service', pricing: { model: 'one_time', range: { min: 0, max: 0 } } },
      { name: 'Retoure', key: 'return', type: 'service', pricing: { model: 'one_time', range: { min: 0, max: 0 } } },
      { name: 'VIP-Kunde', key: 'vip_customer', type: 'service', pricing: { model: 'recurring', range: { min: 0, max: 1000 } } }
    ],
    
    leadStages: ['Website-Besucher', 'Anfrage gestellt', 'Kontaktiert', 'Interessiert', 'Kunde'],
    opportunityStages: ['Produktinteresse', 'Warenkorb', 'Checkout', 'Bestellung', 'Abgeschlossen'],
    
    sampleData: {
      available: true,
      description: 'E-Commerce Beispieldaten mit Kundenanfragen und Bestellungen',
      includes: ['8 Kundenanfragen', '5 aktive Kunden', '3 VIP-Kunden', '10 Bestellungen']
    },
    
    requiredModules: ['crm_core'],
    recommendedModules: ['google_ads', 'business_planning']
  },
  
  {
    id: 'consulting',
    industry: 'consulting',
    name: 'Professional Services',
    description: 'Beratung, Consulting, professionelle Dienstleistungen (B2B)',
    icon: 'Briefcase',
    
    // Professional Services Products (similar to Echomotion)
    defaultProducts: [
      { name: 'Strategy Consulting', key: 'strategy', type: 'consultancy', pricing: { model: 'one_time', range: { min: 5000, max: 50000 } } },
      { name: 'Business Consulting', key: 'business', type: 'consultancy', pricing: { model: 'recurring', range: { min: 2000, max: 10000 } } },
      { name: 'IT Consulting', key: 'it_consulting', type: 'consultancy', pricing: { model: 'recurring', range: { min: 1500, max: 8000 } } },
      { name: 'Management Consulting', key: 'management', type: 'consultancy', pricing: { model: 'one_time', range: { min: 10000, max: 100000 } } },
      { name: 'Project Management', key: 'project_mgmt', type: 'service', pricing: { model: 'recurring', range: { min: 3000, max: 15000 } } },
      { name: 'Training & Workshops', key: 'training', type: 'service', pricing: { model: 'one_time', range: { min: 1000, max: 5000 } } },
      { name: 'Interim Management', key: 'interim', type: 'consultancy', pricing: { model: 'recurring', range: { min: 5000, max: 20000 } } },
      { name: 'Audit Services', key: 'audit', type: 'service', pricing: { model: 'one_time', range: { min: 3000, max: 25000 } } }
    ],
    
    leadStages: ['Erstkontakt', 'Discovery Call', 'Proposal', 'Verhandlung', 'Kunde'],
    opportunityStages: ['Qualifizierung', 'Scoping', 'Proposal', 'Verhandlung', 'Gewonnen'],
    
    sampleData: {
      available: true,
      description: 'Professional Services Beispieldaten mit Beratungs-Projekten',
      includes: ['5 potenzielle Kunden', '8 laufende Projekte', '12 Proposals']
    },
    
    requiredModules: ['crm_core'],
    recommendedModules: ['time_tracking', 'business_planning']
  },
  
  {
    id: 'automotive',
    industry: 'automotive',
    name: 'Automotive Händler',
    description: 'Autohäuser mit hybridem B2C/B2B-Verkauf (Fahrzeuge, Service, Teile)',
    icon: 'Car',
    
    // 24 Products: Vehicles (12), Services (8), Parts (4)
    defaultProducts: [
      // Vehicles (12 categories)
      { name: 'Neuwagen - Kompakt', key: 'new_compact', type: 'service', pricing: { model: 'one_time', range: { min: 15000, max: 25000 } } },
      { name: 'Neuwagen - Limousine', key: 'new_sedan', type: 'service', pricing: { model: 'one_time', range: { min: 25000, max: 40000 } } },
      { name: 'Neuwagen - SUV', key: 'new_suv', type: 'service', pricing: { model: 'one_time', range: { min: 35000, max: 60000 } } },
      { name: 'Neuwagen - Elektro', key: 'new_electric', type: 'service', pricing: { model: 'one_time', range: { min: 30000, max: 80000 } } },
      { name: 'Gebrauchtwagen - Kompakt', key: 'used_compact', type: 'service', pricing: { model: 'one_time', range: { min: 8000, max: 15000 } } },
      { name: 'Gebrauchtwagen - Limousine', key: 'used_sedan', type: 'service', pricing: { model: 'one_time', range: { min: 12000, max: 25000 } } },
      { name: 'Gebrauchtwagen - SUV', key: 'used_suv', type: 'service', pricing: { model: 'one_time', range: { min: 18000, max: 35000 } } },
      { name: 'Nutzfahrzeuge', key: 'commercial', type: 'service', pricing: { model: 'one_time', range: { min: 25000, max: 80000 } } },
      { name: 'Leasing - Privat', key: 'lease_private', type: 'service', pricing: { model: 'recurring', range: { min: 200, max: 800 } } },
      { name: 'Leasing - Geschäftskunden', key: 'lease_business', type: 'service', pricing: { model: 'recurring', range: { min: 300, max: 1500 } } },
      { name: 'Flottenverkauf', key: 'fleet', type: 'service', pricing: { model: 'one_time', range: { min: 100000, max: 1000000 } } },
      { name: 'Finanzierung', key: 'financing', type: 'service', pricing: { model: 'recurring', range: { min: 150, max: 1000 } } },
      
      // Services (8)
      { name: 'Inspektions-Service', key: 'inspection', type: 'service', pricing: { model: 'one_time', range: { min: 80, max: 150 } } },
      { name: 'Ölwechsel', key: 'oil_change', type: 'service', pricing: { model: 'one_time', range: { min: 50, max: 100 } } },
      { name: 'Reifen-Service', key: 'tire_service', type: 'service', pricing: { model: 'one_time', range: { min: 60, max: 500 } } },
      { name: 'Bremsen-Service', key: 'brake_service', type: 'service', pricing: { model: 'one_time', range: { min: 150, max: 800 } } },
      { name: 'Garantie-Verlängerung', key: 'warranty', type: 'service', pricing: { model: 'one_time', range: { min: 500, max: 3000 } } },
      { name: 'Service-Paket', key: 'service_package', type: 'service', pricing: { model: 'recurring', range: { min: 30, max: 100 } } },
      { name: 'Aufbereitung', key: 'detailing', type: 'service', pricing: { model: 'one_time', range: { min: 100, max: 500 } } },
      { name: 'Pannenhilfe', key: 'roadside', type: 'service', pricing: { model: 'recurring', range: { min: 50, max: 150 } } },
      
      // Parts (4)
      { name: 'Original-Teile', key: 'oem_parts', type: 'service', pricing: { model: 'one_time', range: { min: 20, max: 5000 } } },
      { name: 'Aftermarket-Teile', key: 'aftermarket', type: 'service', pricing: { model: 'one_time', range: { min: 15, max: 2000 } } },
      { name: 'Zubehör', key: 'accessories', type: 'service', pricing: { model: 'one_time', range: { min: 10, max: 1000 } } },
      { name: 'Winter-Ausrüstung', key: 'winter', type: 'service', pricing: { model: 'one_time', range: { min: 200, max: 2000 } } }
    ],
    
    leadStages: ['Probefahrt-Anfrage', 'Showroom-Besuch', 'Finanzierungs-Gespräch', 'Kaufbereit', 'Kunde'],
    opportunityStages: ['Interesse', 'Probefahrt geplant', 'Angebot erstellt', 'Finanzierung', 'Geschlossen'],
    
    sampleData: {
      available: true,
      description: 'Automotive Händler Beispieldaten mit Probefahrten, Verkäufen und Service-Terminen',
      includes: ['10 Probefahrt-Anfragen', '5 Fahrzeug-Verkäufe', '8 Service-Termine', '3 Flotten-Opportunities']
    },
    
    requiredModules: ['crm_core'],
    recommendedModules: ['google_ads', 'time_tracking']
  },
  
  {
    id: 'insurance',
    industry: 'insurance',
    name: 'Versicherungsagentur',
    description: 'Versicherungsmakler und -agenturen (B2C, Policen-Verwaltung)',
    icon: 'Shield',
    
    // 7 Insurance Types
    defaultProducts: [
      { name: 'KFZ-Versicherung', key: 'auto', type: 'service', pricing: { model: 'recurring', range: { min: 50, max: 300 } } },
      { name: 'Hausrat-Versicherung', key: 'home', type: 'service', pricing: { model: 'recurring', range: { min: 30, max: 200 } } },
      { name: 'Lebensversicherung', key: 'life', type: 'service', pricing: { model: 'recurring', range: { min: 20, max: 500 } } },
      { name: 'Krankenversicherung', key: 'health', type: 'service', pricing: { model: 'recurring', range: { min: 100, max: 800 } } },
      { name: 'Haftpflicht-Versicherung', key: 'liability', type: 'service', pricing: { model: 'recurring', range: { min: 40, max: 150 } } },
      { name: 'Reiseversicherung', key: 'travel', type: 'service', pricing: { model: 'one_time', range: { min: 10, max: 200 } } },
      { name: 'Gewerbe-Versicherung', key: 'business', type: 'service', pricing: { model: 'recurring', range: { min: 100, max: 2000 } } }
    ],
    
    leadStages: ['Angebot-Anfrage', 'Beratung geplant', 'Policen-Vergleich', 'Unterschriftsbereit', 'Versichert'],
    opportunityStages: ['Erstkontakt', 'Bedarfsanalyse', 'Angebot erstellt', 'Antrag', 'Police aktiv'],
    
    sampleData: {
      available: true,
      description: 'Versicherungs-Agentur Beispieldaten mit Angebots-Anfragen und aktiven Policen',
      includes: ['15 Angebots-Anfragen', '10 aktive Versicherungsnehmer', '5 geplante Beratungen']
    },
    
    requiredModules: ['crm_core'],
    recommendedModules: []  // Insurance specific, no Google Ads
  },
  
  {
    id: 'saas',
    industry: 'saas',
    name: 'SaaS Produkt',
    description: 'Software as a Service mit Subscription-Umsatz (B2B, MRR/ARR-Tracking)',
    icon: 'Cloud',
    
    // Subscription Tiers + Add-ons (8 products)
    defaultProducts: [
      { name: 'Starter-Plan', key: 'starter', type: 'solution', pricing: { model: 'recurring', range: { min: 19, max: 49 } } },
      { name: 'Professional-Plan', key: 'pro', type: 'solution', pricing: { model: 'recurring', range: { min: 99, max: 299 } } },
      { name: 'Business-Plan', key: 'business', type: 'solution', pricing: { model: 'recurring', range: { min: 499, max: 999 } } },
      { name: 'Enterprise-Plan', key: 'enterprise', type: 'solution', pricing: { model: 'recurring', range: { min: 1999, max: 10000 } } },
      { name: 'API-Zugang Add-on', key: 'api', type: 'solution', pricing: { model: 'recurring', range: { min: 50, max: 500 } } },
      { name: 'Premium Support', key: 'support', type: 'service', pricing: { model: 'recurring', range: { min: 100, max: 1000 } } },
      { name: 'Custom Integration', key: 'integration', type: 'development', pricing: { model: 'one_time', range: { min: 2000, max: 20000 } } },
      { name: 'Training & Onboarding', key: 'training', type: 'service', pricing: { model: 'one_time', range: { min: 500, max: 5000 } } }
    ],
    
    leadStages: ['Trial-Anmeldung', 'Produkt aktiviert', 'Engagierter User', 'Upgrade-Absicht', 'Zahlender Kunde'],
    opportunityStages: ['Free Trial', 'Demo geplant', 'POC/Pilot', 'Verhandlung', 'Abonniert'],
    
    sampleData: {
      available: true,
      description: 'SaaS Beispieldaten mit Trial-Usern, Demos und aktiven Subscriptions',
      includes: ['20 Trial-User', '10 Demo-Buchungen', '8 zahlende Kunden', 'MRR/ARR Dashboard-Daten']
    },
    
    requiredModules: ['crm_core'],
    recommendedModules: ['business_planning', 'google_ads']
  }
];

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get all available templates
 */
export function getAllTemplates(): OnboardingTemplate[] {
  return TEMPLATES;
}

/**
 * Get template by ID
 */
export function getTemplateById(templateId: string): OnboardingTemplate | undefined {
  return TEMPLATES.find(t => t.id === templateId);
}

/**
 * Get template for industry
 */
export function getTemplateForIndustry(industry: string): OnboardingTemplate | undefined {
  return TEMPLATES.find(t => t.industry === industry);
}

/**
 * Get template preview data
 */
export function getTemplatePreview(templateId: string): {
  name: string;
  productsCount: number;
  leadStages: string[];
  opportunityStages: string[];
  sampleDataIncludes: string[];
} | undefined {
  const template = getTemplateById(templateId);
  if (!template) return undefined;
  
  return {
    name: template.name,
    productsCount: template.defaultProducts.length,
    leadStages: template.leadStages,
    opportunityStages: template.opportunityStages,
    sampleDataIncludes: template.sampleData.includes
  };
}

